import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";
import { baseApiURL } from "../../baseUrl";
import Heading from "../../components/Heading";
import { 
  FiPlus, FiTrash2, FiBarChart2, FiDownload, FiUpload, FiSettings, FiCheckCircle, FiAlertCircle, FiMenu 
} from "react-icons/fi";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell 
} from 'recharts';

const FinalCOPOAttainment = () => {
  useEffect(() => {
    console.log("FinalCOPOAttainment Component Loaded - Interactive Version");
  }, []);

  const [subjects, setSubjects] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedSemester, setSelectedSemester] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [academicYear, setAcademicYear] = useState("2024-2025");
  
  const userData = useSelector((state) => state.userData);
  const [facultyName, setFacultyName] = useState("");

  useEffect(() => {
    if (userData?.fullname) {
      setFacultyName(userData.fullname);
    }
  }, [userData]);

  // Files
  const [iaFile, setIaFile] = useState(null);
  const [seeFile, setSeeFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  
  // Interactive UI Tabs
  const [activeTab, setActiveTab] = useState("survey"); // survey, co, po, actionPlan, poActionPlan

  // Question Configurations
  const [iaQuestions, setIaQuestions] = useState([{ qName: "1a", co: "CO1", maxMarks: 5 }]);
  const [seeQuestions, setSeeQuestions] = useState([{ qName: "1a", co: "CO1", maxMarks: 10 }]);
  const [assignmentQuestions, setAssignmentQuestions] = useState([
    { qName: "CIA1", co: "CO1", maxMarks: 10 },
    { qName: "CIA2", co: "CO2", maxMarks: 10 }
  ]);

  // Interactive user inputs
  const [cesCounts, setCesCounts] = useState({});
  const [manualIndirect, setManualIndirect] = useState({});
  const [actionPlan, setActionPlan] = useState({});
  const [caym1Actions, setCaym1Actions] = useState(
    Array.from({ length: 7 }, (_, i) => ({ action: "", change: "" }))
  );
  const [poActionPlan, setPoActionPlan] = useState({});
  const [dragInfo, setDragInfo] = useState(null);

  const getQuestionName = (index) => {
    const num = Math.floor(index / 2) + 1;
    const letter = index % 2 === 0 ? "a" : "b";
    return `${num}${letter}`;
  };

  const addQuestion = (type) => {
    let list;
    if (type === "IA") list = iaQuestions;
    else if (type === "SEE") list = seeQuestions;
    else list = assignmentQuestions;

    const nextIndex = list.length;
    const qName = type === "Assignment" ? `CIA${nextIndex + 1}` : getQuestionName(nextIndex);
    const defaultCo = subjectCOs.length > 0 ? subjectCOs[nextIndex % subjectCOs.length]?.coNumber : "";
    const defaultMaxMarks = type === "IA" ? 5 : 10;

    const newQ = { qName, co: defaultCo, maxMarks: defaultMaxMarks };
    if (type === "IA") setIaQuestions([...iaQuestions, newQ]);
    else if (type === "SEE") setSeeQuestions([...seeQuestions, newQ]);
    else setAssignmentQuestions([...assignmentQuestions, newQ]);
  };

  const removeQuestion = (type, index) => {
    if (type === "IA") {
      setIaQuestions(iaQuestions.filter((_, i) => i !== index));
    } else if (type === "SEE") {
      setSeeQuestions(seeQuestions.filter((_, i) => i !== index));
    } else {
      setAssignmentQuestions(assignmentQuestions.filter((_, i) => i !== index));
    }
  };

  const handleQuestionChange = (type, index, field, value) => {
    if (type === "IA") {
      const updated = [...iaQuestions];
      updated[index][field] = value;
      setIaQuestions(updated);
    } else if (type === "SEE") {
      const updated = [...seeQuestions];
      updated[index][field] = value;
      setSeeQuestions(updated);
    } else {
      const updated = [...assignmentQuestions];
      updated[index][field] = value;
      setAssignmentQuestions(updated);
    }
  };

  const handleDragStart = (type, index) => {
    setDragInfo({ type, index });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (type, dropIndex) => {
    if (!dragInfo || dragInfo.type !== type) return;
    const startIndex = dragInfo.index;
    if (startIndex === dropIndex) return;

    let list;
    let setList;
    if (type === "IA") {
      list = [...iaQuestions];
      setList = setIaQuestions;
    } else if (type === "SEE") {
      list = [...seeQuestions];
      setList = setSeeQuestions;
    } else {
      list = [...assignmentQuestions];
      setList = setAssignmentQuestions;
    }

    const [movedItem] = list.splice(startIndex, 1);
    list.splice(dropIndex, 0, movedItem);
    setList(list);
    setDragInfo(null);
  };

  const moveQuestion = (type, index, direction) => {
    let list;
    let setList;
    if (type === "IA") {
      list = [...iaQuestions];
      setList = setIaQuestions;
    } else if (type === "SEE") {
      list = [...seeQuestions];
      setList = setSeeQuestions;
    } else {
      list = [...assignmentQuestions];
      setList = setAssignmentQuestions;
    }

    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= list.length) return;

    const temp = list[index];
    list[index] = list[targetIndex];
    list[targetIndex] = temp;
    setList(list);
  };

  useEffect(() => {
    fetchSubjectsAndBranches();
  }, []);

  const fetchSubjectsAndBranches = async () => {
    try {
      const [subjectsRes, branchesRes] = await Promise.all([
        axios.get(`${baseApiURL()}/subject/getSubject`),
        axios.get(`${baseApiURL()}/branch/getBranch`),
      ]);
      setSubjects(subjectsRes.data?.subject || []);
      setBranches(branchesRes.data?.branches || branchesRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch subjects and branches");
    }
  };

  const [detectedRegulations, setDetectedRegulations] = useState([]);
  const [noStudentsMessage, setNoStudentsMessage] = useState("");

  useEffect(() => {
    detectCohortRegulation();
  }, [selectedBranch, selectedSemester]);

  const detectCohortRegulation = async () => {
    setSelectedSubject("");
    if (!selectedBranch || !selectedSemester) {
      setDetectedRegulations([]);
      setNoStudentsMessage("");
      return;
    }

    try {
      const res = await axios.post(`${baseApiURL()}/student/details/getCohortRegulation`, {
        branch: selectedBranch,
        semester: Number(selectedSemester)
      });

      if (res.data.success && res.data.count > 0 && res.data.regulations?.length > 0) {
        setDetectedRegulations(res.data.regulations);
        if (res.data.academicYear) {
          setAcademicYear(res.data.academicYear);
        }
        setNoStudentsMessage("");
      } else {
        setDetectedRegulations([]);
        setNoStudentsMessage("no student in that semester");
      }
    } catch (error) {
      console.error("Cohort regulation detection error:", error);
      setDetectedRegulations([]);
      setNoStudentsMessage("no student in that semester");
    }
  };

  const filteredSubjects = noStudentsMessage
    ? []
    : subjects.filter((sub) => {
        const semMatch = sub.semester === parseInt(selectedSemester);
        const branchMatch =
          sub.branch?.name === selectedBranch ||
          sub.branch === selectedBranch ||
          (sub.branch?.name && selectedBranch && sub.branch.name.toLowerCase() === selectedBranch.toLowerCase());

        const regMatch =
          detectedRegulations.length > 0
            ? detectedRegulations.some((r) => r.toUpperCase() === sub.regulation?.toUpperCase())
            : true;

        return semMatch && branchMatch && regMatch;
      });

  const selectedSubjectData = subjects.find(s => s._id === selectedSubject);
  const subjectCOs = selectedSubjectData?.courseOutcomes || [];

  useEffect(() => {
    if (!selectedSubject) return;
    const cos = selectedSubjectData?.courseOutcomes || [];
    if (cos && cos.length > 0) {
      const autoIa = cos.map((cItem, i) => ({
        qName: getQuestionName(i),
        co: cItem.coNumber,
        maxMarks: 5
      }));
      setIaQuestions(autoIa);

      const autoSee = cos.map((cItem, i) => ({
        qName: getQuestionName(i),
        co: cItem.coNumber,
        maxMarks: 10
      }));
      setSeeQuestions(autoSee);

      const autoAss = cos.map((cItem, i) => ({
        qName: `CIA${i + 1}`,
        co: cItem.coNumber,
        maxMarks: 10
      }));
      setAssignmentQuestions(autoAss);
    }
  }, [selectedSubject]);

  // Generate IA/SEE Templates
  const handleDownloadTemplate = async (templateType) => {
    if (!selectedBranch || !selectedSemester || !selectedSubject) {
      toast.error("Please select Branch, Semester, and Subject");
      return;
    }

    try {
      setLoading(true);
      toast.loading(`Generating ${templateType.toUpperCase()} template with student data...`);
      
      const branchObj = branches.find(b => b.name === selectedBranch || b._id === selectedBranch);
      const branchVal = branchObj?.name || selectedBranch;

      const payload = {
        branch: branchVal,
        semester: selectedSemester,
        subjectId: selectedSubject,
        facultyName: facultyName,
        academicYear: academicYear,
        iaQuestions: iaQuestions,
        seeQuestions: seeQuestions,
        assignmentQuestions: assignmentQuestions,
        templateType: templateType
      };

      const response = await axios.post(`${baseApiURL()}/obe/template/final-copo/template`, payload, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${templateType.toUpperCase()}_Marks_Template_${branchVal}_Sem${selectedSemester}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.dismiss();
      toast.success(`${templateType.toUpperCase()} template downloaded successfully! Please fill and upload.`);
    } catch (error) {
      toast.dismiss();
      console.error("Error downloading template:", error);
      toast.error("Failed to generate template. Ensure students exist for this branch/semester.");
    } finally {
      setLoading(false);
    }
  };

  // Upload IA and SEE sheets to calculate Direct CO Attainment
  const handleFileUpload = async () => {
    if (!iaFile && !seeFile) {
      toast.error("Please select at least one of IA Marks or SEE Marks files");
      return;
    }
    if (!selectedSubject) {
      toast.error("Please select a Subject");
      return;
    }

    const formData = new FormData();
    if (iaFile) formData.append("iaFile", iaFile);
    if (seeFile) formData.append("seeFile", seeFile);
    formData.append("subjectId", selectedSubject);

    try {
      setLoading(true);
      toast.loading("Calculating initial Direct CO-PO Attainment...");

      const response = await axios.post(
        `${baseApiURL()}/obe/template/final-copo/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        toast.dismiss();
        toast.success(response.data.message);
        
        const initialResults = response.data.results;
        setResults(initialResults);

        // Initialize user inputs
        const initialCes = {};
        const initialIndirect = {};
        const initialActionPlan = {};
        const initialPOAction = {};

        subjectCOs.forEach(co => {
          initialCes[co.coNumber] = { rating1: 0, rating2: 0, rating3: 0 };
          
          // Pre-fill indirect level from calculated initial results
          const calcCo = initialResults.coAttainments.find(c => c.coNumber === co.coNumber);
          initialIndirect[co.coNumber] = calcCo ? calcCo.indirectAttainment : 2;

          initialActionPlan[co.coNumber] = { target: 2.5, observation: "", action: "" };
        });

        // Initialize PO action plan text
        const pos = ['PO1', 'PO2', 'PO3', 'PO4', 'PO5', 'PO6', 'PO7', 'PO8', 'PO9', 'PO10', 'PO11', 'PO12', 'PSO1', 'PSO2', 'PSO3'];
        pos.forEach(po => {
          initialPOAction[po] = "";
        });

        setCesCounts(initialCes);
        setManualIndirect(initialIndirect);
        setActionPlan(initialActionPlan);
        setPoActionPlan(initialPOAction);
        
      } else {
        toast.dismiss();
        toast.error(response.data.message || "Failed to calculate");
      }
    } catch(err) {
      toast.dismiss();
      console.error(err);
      toast.error("Failed to parse and calculate Excel sheets.");
    } finally {
      setLoading(false);
    }
  };

  // Recalculate dynamic CO overall and PO attainments on frontend
  const getRecalculatedData = () => {
    if (!results) return null;

    const coPoMappings = results.coPoMappings || [];
    const studentCount = results.studentCount || 100;
    const directWeight = 0.8;
    const indirectWeight = 0.2;

    // Recalculate CO Attainments
    const newCoAttainments = results.coAttainments.map(co => {
      // Find manual indirect or computed from CES counts
      let indLevel = Number(manualIndirect[co.coNumber]);
      if (isNaN(indLevel)) indLevel = co.indirectAttainment;

      const direct = co.directAttainment;
      const overall = Number((directWeight * direct + indirectWeight * indLevel).toFixed(2));

      return {
        ...co,
        indirectAttainment: indLevel,
        overallAttainment: overall
      };
    });

    // Recalculate PO Attainments
    const pos = ['PO1', 'PO2', 'PO3', 'PO4', 'PO5', 'PO6', 'PO7', 'PO8', 'PO9', 'PO10', 'PO11', 'PO12', 'PSO1', 'PSO2', 'PSO3'];
    const newPoAttainments = [];

    for (const po of pos) {
      const mappings = coPoMappings.filter(m => m.poNumber === po);
      if (mappings.length > 0) {
        let sumDirect = 0;
        let sumIndirect = 0;
        let countMapped = 0;

        mappings.forEach(mapping => {
          const coResult = newCoAttainments.find(r => r.coNumber === mapping.coNumber);
          if (coResult && mapping.strength > 0) {
            sumDirect += (mapping.strength * coResult.directAttainment / 3);
            sumIndirect += (mapping.strength * coResult.indirectAttainment / 3);
            countMapped++;
          }
        });

        if (countMapped > 0) {
          const poDirect = Number((sumDirect / countMapped).toFixed(2));
          const poIndirect = Number((sumIndirect / countMapped).toFixed(2));
          const poAtt = Number((directWeight * poDirect + indirectWeight * poIndirect).toFixed(2));

          newPoAttainments.push({
            poNumber: po,
            attainment: poAtt,
            directAttainment: poDirect,
            indirectAttainment: poIndirect
          });
        }
      }
    }

    return {
      coAttainments: newCoAttainments,
      poAttainments: newPoAttainments
    };
  };

  const currentDynamicData = getRecalculatedData() || results;

  // Handle Course End Survey Input changes
  const handleCesChange = (coNumber, ratingField, value) => {
    const valNum = Math.max(0, parseInt(value) || 0);
    const updatedCes = {
      ...cesCounts,
      [coNumber]: {
        ...cesCounts[coNumber],
        [ratingField]: valNum
      }
    };
    setCesCounts(updatedCes);

    // Dynamic Level Calculation
    const counts = updatedCes[coNumber];
    const totalCount = results?.studentCount || 100;
    const weightedSum = (counts.rating1 * 0.3) + (counts.rating2 * 0.5) + (counts.rating3 * 1.0);
    const percentage = totalCount > 0 ? (weightedSum / totalCount) * 100 : 0;
    
    let indirectLevel = 1;
    if (percentage < 40) indirectLevel = 0;
    else if (percentage >= 60) indirectLevel = 3;
    else if (percentage >= 50) indirectLevel = 2;

    setManualIndirect(prev => ({
      ...prev,
      [coNumber]: indirectLevel
    }));
  };

  // Export fully compiled Excel Report
  const handleExport = async () => {
    if ((!iaFile && !seeFile) || !results || !selectedSubject) {
      toast.error("Required data missing for export");
      return;
    }

    try {
      setLoading(true);
      toast.loading("Generating Final Excel Report...");

      const formData = new FormData();
      if (iaFile) formData.append("iaFile", iaFile);
      if (seeFile) formData.append("seeFile", seeFile);
      formData.append("subjectId", selectedSubject);
      formData.append("academicYear", academicYear);
      formData.append("facultyName", facultyName);
      formData.append("branch", selectedBranch);
      formData.append("semester", selectedSemester);
      
      // Send the recalculated results
      formData.append("results", JSON.stringify(currentDynamicData));
      
      // Send all the UI inputs
      formData.append("uiData", JSON.stringify({
        cesCounts,
        manualIndirect,
        actionPlan,
        caym1Actions,
        poActionPlan
      }));

      const response = await axios.post(
        `${baseApiURL()}/obe/template/final-copo/export`,
        formData,
        {
          responseType: 'blob',
          headers: { "Content-Type": "multipart/form-data" }
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Final_COPO_Report_${selectedSubjectData?.code || 'Results'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.dismiss();
      toast.success("Final report generated and DB updated successfully!");
    } catch (error) {
      toast.dismiss();
      console.error("Export error:", error);
      toast.error("Failed to generate Excel report.");
    } finally {
      setLoading(false);
    }
  };

  // Helper mapping for PO names
  const poKeywords = {
    PO1: "Apply Knowledge",
    PO2: "Solve Problems",
    PO3: "Design/ Development of Solution",
    PO4: "Conduct Investigations",
    PO5: "Use Modern Tools",
    PO6: "Engineer and Society",
    PO7: "Environment and Sustainability",
    PO8: "Professional Ethics",
    PO9: "Individual and Team Work",
    PO10: "Communicate Effectively",
    PO11: "Project Management and Finance",
    PO12: "Life-long Learning",
    PSO1: "PSO 1",
    PSO2: "PSO 2",
    PSO3: "PSO 3"
  };

  return (
    <div className="w-full space-y-6">
      {/* Header Banner */}
      <div className="bento-header-banner flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">Simplified CO-PO Attainment & Interactive Planner</h1>
          <p className="text-slate-500 dark:text-slate-400 font-medium text-xs mt-0.5">Automated calculation and continuous improvement action plan</p>
        </div>
      </div>

      {/* STEP 1: Select Details */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs p-6 border border-slate-200 dark:border-slate-800 w-full space-y-6">
        <h2 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
          <FiSettings className="text-indigo-600 dark:text-indigo-400" /> Step 1: Mappings & Selection
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">Academic Year</label>
              {academicYear && (
                <span className="text-[10px] bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 px-2 py-0.5 rounded font-bold border border-indigo-100 dark:border-indigo-800">
                  Auto-fetched
                </span>
              )}
            </div>
            <input
              type="text"
              className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-indigo-900 dark:text-indigo-300 focus:ring-2 focus:ring-indigo-500 outline-none"
              value={academicYear}
              onChange={(e) => setAcademicYear(e.target.value)}
              placeholder="e.g. 2024-2025"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Branch</label>
            <select
              className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
            >
              <option value="">-- Select Branch --</option>
              {branches.map((b) => (
                <option key={b._id} value={b.name}>{b.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Semester</label>
            <select
              className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
              value={selectedSemester}
              onChange={(e) => setSelectedSemester(e.target.value)}
            >
              <option value="">-- Select Semester --</option>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((s) => (
                <option key={s} value={s}>Semester {s}</option>
              ))}
            </select>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">Student Regulation</label>
              {detectedRegulations.length > 0 && (
                <span className="text-[10px] bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 rounded font-bold border border-emerald-100 dark:border-emerald-800">
                  Auto-fetched
                </span>
              )}
            </div>
            <input
              type="text"
              readOnly
              value={detectedRegulations.length > 0 ? detectedRegulations.join(", ") : "Auto-detecting..."}
              className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-emerald-800 dark:text-emerald-400 cursor-not-allowed"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Subject</label>
            <select
              className={`w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border rounded-xl text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none ${
                noStudentsMessage ? "border-rose-300 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-400 font-medium" : "border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white"
              }`}
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              disabled={!selectedBranch || !selectedSemester || !!noStudentsMessage}
            >
              <option value="">
                {!selectedBranch || !selectedSemester
                  ? "-- Select Subject --"
                  : noStudentsMessage
                  ? "no student in that semester"
                  : filteredSubjects.length === 0
                  ? "No subjects available for detected regulation"
                  : "-- Select Subject --"}
              </option>
              {filteredSubjects.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.name} ({s.code}) - {s.regulation}
                </option>
              ))}
            </select>
            {noStudentsMessage && (
              <p className="text-xs text-rose-500 dark:text-rose-400 mt-1 font-semibold flex items-center gap-1">
                <FiAlertCircle /> {noStudentsMessage}
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Faculty Name</label>
            <input
              type="text"
              className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
              value={facultyName}
              onChange={(e) => setFacultyName(e.target.value)}
              placeholder="e.g. Dr. Kiran B. Malagi"
            />
          </div>
        </div>

        {/* STEP 2: Configure Questions */}
        <div className="border-t border-slate-100 dark:border-slate-800 pt-6">
          <h2 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-4">Step 2: Mapped Exam Configuration</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* IA Config */}
            <div className="bg-slate-50 dark:bg-slate-950 p-5 rounded-xl border border-slate-200 dark:border-slate-800">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">IA Questions</h3>
                <button
                  onClick={() => addQuestion("IA")}
                  className="flex items-center gap-1 text-xs bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 px-3 py-1.5 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/60 transition font-bold border border-indigo-100 dark:border-indigo-800 cursor-pointer"
                >
                  <FiPlus /> Add Row
                </button>
              </div>
              <div className="space-y-2.5 max-h-[300px] overflow-y-auto pr-1">
                {iaQuestions.map((q, idx) => (
                  <div
                    key={idx}
                    draggable
                    onDragStart={() => handleDragStart("IA", idx)}
                    onDragOver={handleDragOver}
                    onDrop={() => handleDrop("IA", idx)}
                    className="flex gap-2 items-center bg-white dark:bg-slate-900 p-2.5 rounded-xl shadow-xs border border-slate-200 dark:border-slate-800 hover:border-indigo-300 dark:hover:border-indigo-700 transition"
                  >
                    <div className="flex flex-col justify-center items-center text-slate-400 dark:text-slate-500 hover:text-slate-600 px-1 select-none">
                      <FiMenu className="text-sm cursor-grab active:cursor-grabbing" title="Drag to reorder" />
                      <div className="flex gap-0.5 mt-0.5">
                        <button
                          type="button"
                          disabled={idx === 0}
                          onClick={() => moveQuestion("IA", idx, -1)}
                          className="text-[9px] px-1 py-0.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded disabled:opacity-30 cursor-pointer"
                          title="Move Up"
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          disabled={idx === iaQuestions.length - 1}
                          onClick={() => moveQuestion("IA", idx, 1)}
                          className="text-[9px] px-1 py-0.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded disabled:opacity-30 cursor-pointer"
                          title="Move Down"
                        >
                          ▼
                        </button>
                      </div>
                    </div>

                    <div className="w-1/3">
                      <label className="block text-[10px] font-semibold text-slate-400 dark:text-slate-500 mb-0.5">Q. No.</label>
                      <input
                        type="text"
                        placeholder="e.g. 1a"
                        value={q.qName}
                        onChange={(e) => handleQuestionChange("IA", idx, "qName", e.target.value)}
                        className="w-full text-xs px-2.5 py-1.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 outline-none"
                      />
                    </div>
                    <div className="w-1/3">
                      <label className="block text-[10px] font-semibold text-slate-400 dark:text-slate-500 mb-0.5">CO Mapped</label>
                      <select
                        value={q.co}
                        onChange={(e) => handleQuestionChange("IA", idx, "co", e.target.value)}
                        className="w-full text-xs px-2.5 py-1.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 outline-none"
                      >
                        <option value="">Select CO</option>
                        {subjectCOs.map((coItem) => (
                          <option key={coItem.coNumber} value={coItem.coNumber}>
                            {coItem.coNumber}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="w-1/3">
                      <label className="block text-[10px] font-semibold text-slate-400 dark:text-slate-500 mb-0.5">Max Marks</label>
                      <input
                        type="number"
                        placeholder="e.g. 5"
                        value={q.maxMarks}
                        onChange={(e) => handleQuestionChange("IA", idx, "maxMarks", Number(e.target.value))}
                        className="w-full text-xs px-2.5 py-1.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 outline-none"
                      />
                    </div>
                    <button
                      onClick={() => removeQuestion("IA", idx)}
                      className="mt-4 p-1.5 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/60 rounded-lg transition cursor-pointer"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* SEE Config */}
            <div className="bg-slate-50 dark:bg-slate-950 p-5 rounded-xl border border-slate-200 dark:border-slate-800">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider">SEE Questions</h3>
                <button
                  onClick={() => addQuestion("SEE")}
                  className="flex items-center gap-1 text-xs bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 px-3 py-1.5 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/60 transition font-bold border border-indigo-100 dark:border-indigo-800 cursor-pointer"
                >
                  <FiPlus /> Add Row
                </button>
              </div>
              <div className="space-y-2.5 max-h-[300px] overflow-y-auto pr-1">
                {seeQuestions.map((q, idx) => (
                  <div
                    key={idx}
                    draggable
                    onDragStart={() => handleDragStart("SEE", idx)}
                    onDragOver={handleDragOver}
                    onDrop={() => handleDrop("SEE", idx)}
                    className="flex gap-2 items-center bg-white dark:bg-slate-900 p-2.5 rounded-xl shadow-xs border border-slate-200 dark:border-slate-800 hover:border-indigo-300 dark:hover:border-indigo-700 transition"
                  >
                    <div className="flex flex-col justify-center items-center text-slate-400 dark:text-slate-500 hover:text-slate-600 px-1 select-none">
                      <FiMenu className="text-sm cursor-grab active:cursor-grabbing" title="Drag to reorder" />
                      <div className="flex gap-0.5 mt-0.5">
                        <button
                          type="button"
                          disabled={idx === 0}
                          onClick={() => moveQuestion("SEE", idx, -1)}
                          className="text-[9px] px-1 py-0.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded disabled:opacity-30 cursor-pointer"
                          title="Move Up"
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          disabled={idx === seeQuestions.length - 1}
                          onClick={() => moveQuestion("SEE", idx, 1)}
                          className="text-[9px] px-1 py-0.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded disabled:opacity-30 cursor-pointer"
                          title="Move Down"
                        >
                          ▼
                        </button>
                      </div>
                    </div>

                    <div className="w-1/3">
                      <label className="block text-[10px] font-semibold text-slate-400 dark:text-slate-500 mb-0.5">Q. No.</label>
                      <input
                        type="text"
                        placeholder="e.g. 1a"
                        value={q.qName}
                        onChange={(e) => handleQuestionChange("SEE", idx, "qName", e.target.value)}
                        className="w-full text-xs px-2.5 py-1.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 outline-none"
                      />
                    </div>
                    <div className="w-1/3">
                      <label className="block text-[10px] font-semibold text-slate-400 dark:text-slate-500 mb-0.5">CO Mapped</label>
                      <select
                        value={q.co}
                        onChange={(e) => handleQuestionChange("SEE", idx, "co", e.target.value)}
                        className="w-full text-xs px-2.5 py-1.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 outline-none"
                      >
                        <option value="">Select CO</option>
                        {subjectCOs.map((coItem) => (
                          <option key={coItem.coNumber} value={coItem.coNumber}>
                            {coItem.coNumber}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="w-1/3">
                      <label className="block text-[10px] font-semibold text-slate-400 dark:text-slate-500 mb-0.5">Max Marks</label>
                      <input
                        type="number"
                        placeholder="e.g. 10"
                        value={q.maxMarks}
                        onChange={(e) => handleQuestionChange("SEE", idx, "maxMarks", Number(e.target.value))}
                        className="w-full text-xs px-2.5 py-1.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 outline-none"
                      />
                    </div>
                    <button
                      onClick={() => removeQuestion("SEE", idx)}
                      className="mt-4 p-1.5 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/60 rounded-lg transition cursor-pointer"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                ))}
              </div>
            </div>

          </div>

          <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-800">
            <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-3 flex items-center gap-2">
              <FiPlus className="text-indigo-600 dark:text-indigo-400" /> Assignment / CIA Configuration
            </h3>
            <div className="bg-slate-50 dark:bg-slate-950 p-5 rounded-xl border border-slate-200 dark:border-slate-800">
              <div className="flex justify-between items-center mb-3">
                <p className="text-xs text-slate-500 dark:text-slate-400">Configure continuous internal assessments (CIA1-CIA7)</p>
                <button
                  onClick={() => addQuestion("Assignment")}
                  className="flex items-center gap-1 text-xs bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 px-3 py-1.5 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/60 transition font-bold border border-indigo-100 dark:border-indigo-800 cursor-pointer"
                >
                  <FiPlus /> Add CIA
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 max-h-[250px] overflow-y-auto pr-1">
                {assignmentQuestions.map((q, idx) => (
                  <div
                    key={idx}
                    draggable
                    onDragStart={() => handleDragStart("Assignment", idx)}
                    onDragOver={handleDragOver}
                    onDrop={() => handleDrop("Assignment", idx)}
                    className="flex gap-2 items-center bg-white dark:bg-slate-900 p-2.5 rounded-xl shadow-xs border border-slate-200 dark:border-slate-800 hover:border-indigo-300 dark:hover:border-indigo-700 transition"
                  >
                    <div className="flex flex-col justify-center items-center text-slate-400 dark:text-slate-500 select-none pr-1">
                      <FiMenu className="text-xs cursor-grab active:cursor-grabbing" title="Drag to reorder" />
                    </div>
                    <div className="flex-1 space-y-1">
                      <input
                        type="text"
                        placeholder="CIA Name"
                        value={q.qName}
                        onChange={(e) => handleQuestionChange("Assignment", idx, "qName", e.target.value)}
                        className="w-full text-xs px-2 py-1 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 outline-none"
                      />
                      <div className="flex gap-1.5">
                        <select
                          value={q.co}
                          onChange={(e) => handleQuestionChange("Assignment", idx, "co", e.target.value)}
                          className="w-1/2 text-[10px] px-1 py-1 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded text-slate-900 dark:text-white"
                        >
                          <option value="">CO</option>
                          {subjectCOs.map((coItem) => (
                            <option key={coItem.coNumber} value={coItem.coNumber}>{coItem.coNumber}</option>
                          ))}
                        </select>
                        <input
                          type="number"
                          placeholder="Max"
                          value={q.maxMarks}
                          onChange={(e) => handleQuestionChange("Assignment", idx, "maxMarks", Number(e.target.value))}
                          className="w-1/2 text-[10px] px-1 py-1 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded text-slate-900 dark:text-white"
                        />
                      </div>
                    </div>
                    <button onClick={() => removeQuestion("Assignment", idx)} className="text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/60 p-1 rounded cursor-pointer">
                      <FiTrash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* STEP 3: Download Split Templates */}
        <div className="flex flex-wrap gap-4 justify-between border-b border-slate-100 dark:border-slate-800 pb-6">
          <div>
            <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">Download Simplified Excel Templates</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Fill IA and SEE marks separately with student names pre-populated.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleDownloadTemplate("ia")}
              disabled={loading}
              className="px-4 py-2 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 rounded-xl font-bold text-xs hover:bg-indigo-100 dark:hover:bg-indigo-900/60 disabled:opacity-50 flex items-center gap-1.5 border border-indigo-100 dark:border-indigo-800 transition cursor-pointer"
            >
              <FiDownload /> Download IA Template
            </button>
            <button
              onClick={() => handleDownloadTemplate("see")}
              disabled={loading}
              className="px-4 py-2 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 rounded-xl font-bold text-xs hover:bg-emerald-100 dark:hover:bg-emerald-900/60 disabled:opacity-50 flex items-center gap-1.5 border border-emerald-100 dark:border-emerald-800 transition cursor-pointer"
            >
              <FiDownload /> Download SEE Template
            </button>
          </div>
        </div>

        {/* STEP 4: Upload Marks and Calculate */}
        <div className="pt-2">
          <h2 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-3 flex items-center gap-2">
            <FiUpload className="text-emerald-600 dark:text-emerald-400" /> Step 3: Upload filled Marks sheets
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="border border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-4 bg-slate-50 dark:bg-slate-950">
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">IA Marks Filled File</label>
              <input
                type="file"
                accept=".xlsx, .xls"
                onChange={(e) => setIaFile(e.target.files[0])}
                className="w-full text-xs text-slate-500 dark:text-slate-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-indigo-50 dark:file:bg-indigo-950/60 file:text-indigo-700 dark:file:text-indigo-300 hover:file:bg-indigo-100 border border-slate-200 dark:border-slate-800 rounded-lg cursor-pointer"
              />
            </div>
            <div className="border border-dashed border-slate-300 dark:border-slate-700 rounded-xl p-4 bg-slate-50 dark:bg-slate-950">
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-2">SEE Marks Filled File</label>
              <input
                type="file"
                accept=".xlsx, .xls"
                onChange={(e) => setSeeFile(e.target.files[0])}
                className="w-full text-xs text-slate-500 dark:text-slate-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-emerald-50 dark:file:bg-emerald-950/60 file:text-emerald-700 dark:file:text-emerald-300 hover:file:bg-emerald-100 border border-slate-200 dark:border-slate-800 rounded-lg cursor-pointer"
              />
            </div>
          </div>
          
          <div className="flex justify-end">
            <button
              onClick={handleFileUpload}
              disabled={loading || (!iaFile && !seeFile)}
              className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold text-xs disabled:opacity-50 transition shadow-xs flex items-center gap-2 cursor-pointer"
            >
              <FiCheckCircle /> Calculate Direct Attainment
            </button>
          </div>
        </div>
      </div>

      {/* STEP 5: Interactive Calculation & Data Tabs */}
      {results && currentDynamicData && (
        <div className="w-full space-y-6">
          
          {/* TAB NAVIGATION */}
          <div className="flex flex-wrap border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-1.5 rounded-2xl gap-2 shadow-xs">
            {[
              { id: "survey", label: "Course End Survey (CES)" },
              { id: "co", label: "CO Attainment Summary" },
              { id: "po", label: "PO Attainment & Charts" },
              { id: "actionPlan", label: "CO Action Plan" },
              { id: "poActionPlan", label: "PO Action Plan" }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition cursor-pointer ${
                  activeTab === tab.id
                    ? "bg-indigo-600 text-white shadow-xs"
                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs p-6 border border-slate-200 dark:border-slate-800 min-h-[400px]">
            
            {/* TAB 1: COURSE END SURVEY */}
            {activeTab === "survey" && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-base font-bold text-slate-900 dark:text-white">Course End Survey (CES) Inputs</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    Provide student counts for each rating level. Class strength is tracked as <span className="font-bold text-indigo-600 dark:text-indigo-400">{results.studentCount} students</span>.
                  </p>
                </div>
                <div className="overflow-x-auto border border-slate-200 dark:border-slate-800 rounded-2xl">
                  <table className="min-w-full text-left text-xs whitespace-nowrap divide-y divide-slate-200 dark:divide-slate-800">
                    <thead className="bg-indigo-50/50 dark:bg-indigo-950/40 text-indigo-900 dark:text-indigo-300 font-bold uppercase tracking-wider">
                      <tr>
                        <th className="px-5 py-3">Course Outcome</th>
                        <th className="px-5 py-3 text-center">Rating 1: Some Ability</th>
                        <th className="px-5 py-3 text-center">Rating 2: Adequate</th>
                        <th className="px-5 py-3 text-center">Rating 3: More than Adequate</th>
                        <th className="px-5 py-3 text-center">Total Responses</th>
                        <th className="px-5 py-3 text-center">CES Weighted %</th>
                        <th className="px-5 py-3 text-center text-indigo-700 dark:text-indigo-300">Calculated Indirect Level</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
                      {subjectCOs.map(co => {
                        const counts = cesCounts[co.coNumber] || { rating1: 0, rating2: 0, rating3: 0 };
                        const totalRes = counts.rating1 + counts.rating2 + counts.rating3;
                        const wSum = (counts.rating1 * 0.3) + (counts.rating2 * 0.5) + (counts.rating3 * 1.0);
                        const perc = results.studentCount > 0 ? ((wSum / results.studentCount) * 100).toFixed(2) : 0;
                        const indLevel = manualIndirect[co.coNumber] || 0;

                        return (
                          <tr key={co.coNumber} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                            <td className="px-5 py-3.5 font-bold text-slate-900 dark:text-white">{co.coNumber}</td>
                            <td className="px-5 py-3.5 text-center">
                              <input
                                type="number"
                                value={counts.rating1}
                                onChange={(e) => handleCesChange(co.coNumber, "rating1", e.target.value)}
                                className="w-16 px-2 py-1 text-center bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 outline-none"
                              />
                            </td>
                            <td className="px-5 py-3.5 text-center">
                              <input
                                type="number"
                                value={counts.rating2}
                                onChange={(e) => handleCesChange(co.coNumber, "rating2", e.target.value)}
                                className="w-16 px-2 py-1 text-center bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 outline-none"
                              />
                            </td>
                            <td className="px-5 py-3.5 text-center">
                              <input
                                type="number"
                                value={counts.rating3}
                                onChange={(e) => handleCesChange(co.coNumber, "rating3", e.target.value)}
                                className="w-16 px-2 py-1 text-center bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 outline-none"
                              />
                            </td>
                            <td className="px-5 py-3.5 text-center font-bold text-slate-700 dark:text-slate-300">{totalRes}</td>
                            <td className="px-5 py-3.5 text-center font-bold text-slate-700 dark:text-slate-300">{perc}%</td>
                            <td className="px-5 py-3.5 text-center font-bold text-indigo-600 dark:text-indigo-400 text-sm">
                              {indLevel}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* TAB 2: CO ATTAINMENT */}
            {activeTab === "co" && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-base font-bold text-slate-900 dark:text-white">Final CO Attainment Summary</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    Direct Attainment is computed from uploads. Modify the **Indirect Attainment** level below if you need manual overrides; Overall CO and PO values update live.
                  </p>
                </div>
                <div className="overflow-x-auto border border-slate-200 dark:border-slate-800 rounded-2xl">
                  <table className="min-w-full text-left text-xs whitespace-nowrap divide-y divide-slate-200 dark:divide-slate-800">
                    <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider">
                      <tr>
                        <th className="px-5 py-3">Course Outcome</th>
                        <th className="px-5 py-3 text-center">IA Level</th>
                        <th className="px-5 py-3 text-center">Assignment Level</th>
                        <th className="px-5 py-3 text-center">SEE Level</th>
                        <th className="px-5 py-3 text-center">Direct Attainment (80%)</th>
                        <th className="px-5 py-3 text-center text-amber-600 dark:text-amber-400">Indirect Attainment (20%) [Editable]</th>
                        <th className="px-5 py-3 font-bold text-indigo-600 dark:text-indigo-400 text-right">Overall CO Attainment</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
                      {currentDynamicData.coAttainments.map((co) => (
                        <tr key={co.coNumber} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                          <td className="px-5 py-3.5 font-bold text-slate-900 dark:text-white">{co.coNumber}</td>
                          <td className="px-5 py-3.5 text-center">{co.iaLevel}</td>
                          <td className="px-5 py-3.5 text-center text-purple-600 dark:text-purple-400 font-bold">{co.assignmentLevel || 0}</td>
                          <td className="px-5 py-3.5 text-center">{co.seeLevel}</td>
                          <td className="px-5 py-3.5 text-center font-bold text-slate-700 dark:text-slate-300">{co.directAttainment}</td>
                          <td className="px-5 py-3.5 text-center">
                            <input
                              type="number"
                              min="0"
                              max="3"
                              step="0.1"
                              value={manualIndirect[co.coNumber] ?? co.indirectAttainment}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value);
                                setManualIndirect(prev => ({
                                  ...prev,
                                  [co.coNumber]: isNaN(val) ? "" : val
                                }));
                              }}
                              className="w-20 px-2 py-1 text-center font-bold text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 rounded-lg focus:ring-1 focus:ring-amber-500 outline-none"
                            />
                          </td>
                          <td className="px-5 py-3.5 font-bold text-indigo-600 dark:text-indigo-400 text-right text-base">{co.overallAttainment}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* TAB 3: PO ATTAINMENT */}
            {activeTab === "po" && (
              <div className="space-y-6">
                <h3 className="text-base font-bold text-purple-900 dark:text-purple-300">PO/PSO Attainment Summary</h3>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  <div className="overflow-x-auto border border-purple-200 dark:border-purple-800/60 rounded-2xl">
                    <table className="min-w-full text-left text-xs whitespace-nowrap divide-y divide-purple-100 dark:divide-purple-900/40">
                      <thead className="bg-purple-50/60 dark:bg-purple-950/40 text-purple-900 dark:text-purple-300 font-bold uppercase tracking-wider">
                        <tr>
                          <th className="px-5 py-3">Program Outcome</th>
                          <th className="px-5 py-3 text-center">Direct Attainment</th>
                          <th className="px-5 py-3 text-center">Indirect Attainment</th>
                          <th className="px-5 py-3 font-bold text-right text-purple-900 dark:text-purple-200">Total Attainment (Max 3.0)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-purple-100 dark:divide-purple-900/40 text-slate-800 dark:text-slate-200">
                        {currentDynamicData.poAttainments.map((po) => (
                          <tr key={po.poNumber} className="hover:bg-purple-50/50 dark:hover:bg-purple-950/20 transition-colors">
                            <td className="px-5 py-3.5 font-medium text-slate-900 dark:text-white">
                              <span className="font-bold">{po.poNumber}:</span> {poKeywords[po.poNumber] || ""}
                            </td>
                            <td className="px-5 py-3.5 text-center text-indigo-600 dark:text-indigo-400 font-bold">{po.directAttainment || "-"}</td>
                            <td className="px-5 py-3.5 text-center text-amber-600 dark:text-amber-400 font-bold">{po.indirectAttainment || "-"}</td>
                            <td className="px-5 py-3.5 font-bold text-purple-600 dark:text-purple-400 text-right text-sm">{po.attainment}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* PO Attainment Chart */}
                  <div className="bg-slate-50 dark:bg-slate-950 rounded-2xl p-5 border border-slate-200 dark:border-slate-800 min-h-[400px] flex flex-col justify-between">
                    <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider text-center mb-4">Attainment Chart</h4>
                    <div className="h-[350px] w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={currentDynamicData.poAttainments} margin={{ bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="poNumber" angle={-45} textAnchor="end" interval={0} height={50} />
                          <YAxis domain={[0, 3]} ticks={[0, 0.5, 1, 1.5, 2, 2.5, 3]} />
                          <Tooltip />
                          <Legend verticalAlign="top" height={36}/>
                          <Bar name="Direct" dataKey="directAttainment" fill="#6366F1" radius={[4, 4, 0, 0]} />
                          <Bar name="Indirect" dataKey="indirectAttainment" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                          <Bar name="Overall Attainment" dataKey="attainment" fill="#10B981" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 4: ACTION PLAN */}
            {activeTab === "actionPlan" && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-base font-bold text-slate-900 dark:text-white mb-3">Observations and Action Plan (Direct)</h3>
                  <div className="overflow-x-auto border border-slate-200 dark:border-slate-800 rounded-2xl">
                    <table className="min-w-full text-left text-xs whitespace-nowrap divide-y divide-slate-200 dark:divide-slate-800">
                      <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider">
                        <tr>
                          <th className="px-5 py-3 w-1/12">Course Outcome</th>
                          <th className="px-5 py-3 text-center w-2/12">Target [Set Manually]</th>
                          <th className="px-5 py-3 text-center w-2/12">Attainment</th>
                          <th className="px-5 py-3 text-center w-2/12">Gap</th>
                          <th className="px-5 py-3 w-3/12">Observation</th>
                          <th className="px-5 py-3 w-3/12">Action Proposed</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
                        {currentDynamicData.coAttainments.map(co => {
                          const plan = actionPlan[co.coNumber] || { target: 2.5, observation: "", action: "" };
                          const gap = Number((plan.target - co.overallAttainment).toFixed(2));

                          return (
                            <tr key={co.coNumber} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                              <td className="px-5 py-3.5 font-bold text-slate-900 dark:text-white">{co.coNumber}</td>
                              <td className="px-5 py-3.5 text-center">
                                <input
                                  type="number"
                                  step="0.1"
                                  value={plan.target}
                                  onChange={(e) => {
                                    const val = parseFloat(e.target.value);
                                    setActionPlan(prev => ({
                                      ...prev,
                                      [co.coNumber]: {
                                        ...prev[co.coNumber],
                                        target: isNaN(val) ? "" : val
                                      }
                                    }));
                                  }}
                                  className="w-16 px-2 py-1 text-center bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 outline-none"
                                />
                              </td>
                              <td className="px-5 py-3.5 text-center font-bold text-slate-700 dark:text-slate-300">{co.overallAttainment}</td>
                              <td className="px-5 py-3.5 text-center font-bold text-rose-600 dark:text-rose-400">{gap > 0 ? gap : 0}</td>
                              <td className="px-5 py-3.5">
                                <textarea
                                  value={plan.observation}
                                  onChange={(e) => setActionPlan(prev => ({
                                    ...prev,
                                    [co.coNumber]: {
                                      ...prev[co.coNumber],
                                      observation: e.target.value
                                    }
                                  }))}
                                  placeholder="Observations..."
                                  rows="2"
                                  className="w-full text-xs p-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 outline-none"
                                />
                              </td>
                              <td className="px-5 py-3.5">
                                <textarea
                                  value={plan.action}
                                  onChange={(e) => setActionPlan(prev => ({
                                    ...prev,
                                    [co.coNumber]: {
                                      ...prev[co.coNumber],
                                      action: e.target.value
                                    }
                                  }))}
                                  placeholder="Action plan proposed..."
                                  rows="2"
                                  className="w-full text-xs p-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 outline-none"
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Secondary Table: Outcomes on Actions for CAYm1 Observations/Suggestions */}
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200 mb-2">Outcomes on Actions for CAYm1 Observations/Suggestions</h4>
                  <div className="overflow-x-auto border border-slate-200 dark:border-slate-800 rounded-2xl">
                    <table className="min-w-full text-left text-xs whitespace-nowrap divide-y divide-slate-200 dark:divide-slate-800">
                      <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider">
                        <tr>
                          <th className="px-5 py-3 w-1/12 text-center">SI. No.</th>
                          <th className="px-5 py-3 w-5/12">Action Taken</th>
                          <th className="px-5 py-3 w-6/12">Change Observed</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
                        {caym1Actions.map((item, idx) => (
                          <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                            <td className="px-5 py-3.5 text-center font-bold text-slate-400">{idx + 1}</td>
                            <td className="px-5 py-3.5">
                              <input
                                type="text"
                                value={item.action}
                                onChange={(e) => {
                                  const updated = [...caym1Actions];
                                  updated[idx].action = e.target.value;
                                  setCaym1Actions(updated);
                                }}
                                placeholder={`Action ${idx + 1}`}
                                className="w-full text-xs p-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 outline-none"
                              />
                            </td>
                            <td className="px-5 py-3.5">
                              <input
                                type="text"
                                value={item.change}
                                onChange={(e) => {
                                  const updated = [...caym1Actions];
                                  updated[idx].change = e.target.value;
                                  setCaym1Actions(updated);
                                }}
                                placeholder="Change observed..."
                                className="w-full text-xs p-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 outline-none"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 5: PO ATTAINMENT ACTION PLAN */}
            {activeTab === "poActionPlan" && (
              <div className="space-y-4">
                <h3 className="text-base font-bold text-slate-900 dark:text-white">Observations and Action Plan (POs & PSOs)</h3>
                <div className="overflow-x-auto border border-slate-200 dark:border-slate-800 rounded-2xl">
                  <table className="min-w-full text-left text-xs whitespace-nowrap divide-y divide-slate-200 dark:divide-slate-800">
                    <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider">
                      <tr>
                        <th className="px-5 py-3 w-2/12">Program Outcome</th>
                        <th className="px-5 py-3 text-center w-2/12">Attained</th>
                        <th className="px-5 py-3 text-center w-2/12">Target (Avg Map Strength)</th>
                        <th className="px-5 py-3 w-6/12">Action Plan</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
                      {currentDynamicData.poAttainments.map((po) => {
                        const maps = results.coPoMappings.filter(m => m.poNumber === po.poNumber);
                        const targetVal = maps.length > 0
                          ? Number((maps.reduce((s, m) => s + m.strength, 0) / maps.length).toFixed(2))
                          : 0;

                        return (
                          <tr key={po.poNumber} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                            <td className="px-5 py-3.5 font-bold text-slate-900 dark:text-white">
                              <span className="font-bold">{po.poNumber}:</span> {poKeywords[po.poNumber] || ""}
                            </td>
                            <td className="px-5 py-3.5 text-center font-bold text-purple-600 dark:text-purple-400 text-sm">{po.attainment}</td>
                            <td className="px-5 py-3.5 text-center font-bold text-slate-500 dark:text-slate-400">{targetVal}</td>
                            <td className="px-5 py-3.5">
                              <textarea
                                value={poActionPlan[po.poNumber] || ""}
                                onChange={(e) => setPoActionPlan(prev => ({
                                  ...prev,
                                  [po.poNumber]: e.target.value
                                }))}
                                placeholder={`Action plan details for ${po.poNumber}...`}
                                rows="2"
                                className="w-full text-xs p-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 outline-none"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          </div>

          {/* FINAL EXPORT REPORT BUTTON */}
          <div className="flex justify-center bg-indigo-50 dark:bg-indigo-950/40 p-6 rounded-2xl border border-indigo-200 dark:border-indigo-800">
            <div className="text-center space-y-3">
              <h3 className="text-base font-bold text-indigo-900 dark:text-indigo-200">Compile Calculations & Download Final Report</h3>
              <p className="text-xs text-indigo-700 dark:text-indigo-400 max-w-lg mx-auto">
                This will save the final calculated Overall CO and PO Attainments into the Subject Database, compile all tabs (CES survey counts, Observations, Action Plans) into the base multi-sheet Excel file containing analysis graphs, and prompt for download.
              </p>
              <button
                onClick={handleExport}
                disabled={loading}
                className="px-8 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold text-xs transition shadow-xs flex items-center gap-2 mx-auto cursor-pointer"
              >
                <FiBarChart2 className="text-lg" /> {loading ? "Generating Report..." : "Download Final CO-PO Excel Report"}
              </button>
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default FinalCOPOAttainment;
